import { useState } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { loginPin } from "@/lib/api";
import { useTrip } from "@/lib/store";

const Brand = ({ children }) => (
  <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[var(--jade-deep)] px-6 py-10 text-center text-[var(--parchment)]">
    <div className="grain absolute inset-0" />
    <div className="relative w-full max-w-sm">
      <p className="eyebrow !text-[var(--brass)]">Mary's Birthday</p>
      <h1 className="font-display mt-1 text-5xl font-semibold leading-[0.95]"><span className="italic font-medium">China</span> Adventure</h1>
      <p className="mt-2 text-xs text-[var(--parchment)]/60">Hong Kong • Shenzhen • Yangshuo • Guangzhou · 20–29 Oct 2026</p>
      {children}
    </div>
  </div>
);

export function ProfilePicker({ onDone }) {
  const { profiles, setProfileId, refresh } = useTrip();
  return (
    <Brand>
      <p className="eyebrow mt-10 !text-[var(--parchment)]/70">Who's travelling?</p>
      <div data-testid="profile-picker" className="mt-4 grid grid-cols-3 gap-4">
        {profiles.map((p, i) => (
          <motion.button key={p.id} data-testid={`profile-${p.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} onClick={() => { setProfileId(p.id); onDone?.(); }} className="tap flex flex-col items-center gap-2">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[var(--parchment)]/30 font-display text-2xl font-semibold" style={{ background: p.color }}>{p.initial || p.name[0]}</span>
            <span className="text-sm font-semibold">{p.name}</span>
          </motion.button>
        ))}
        {!profiles.length && <button onClick={refresh} className="col-span-3 text-sm underline">Loading travellers… tap to retry</button>}
      </div>
      <p className="mt-8 text-[11px] text-[var(--parchment)]/50">We'll remember you on this phone. Switch anytime in Settings.</p>
    </Brand>
  );
}

export function AccessGate({ children }) {
  const { access, setAccess, profile, trip, refresh } = useTrip();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!access) {
    const submit = async (e) => {
      e.preventDefault(); setBusy(true); setErr("");
      try { await loginPin(pin); setAccess(true); refresh(); } catch { setErr("That PIN isn't right — ask Angelina."); }
      setBusy(false);
    };
    return (
      <Brand>
        <form onSubmit={submit} className="mt-10">
          <p className="eyebrow !text-[var(--parchment)]/70 flex items-center justify-center gap-1"><Lock className="h-3 w-3" /> Family trip PIN</p>
          <input data-testid="pin-input" autoFocus type="password" inputMode="numeric" autoComplete="one-time-code" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" className="mt-3 h-16 w-full rounded-2xl border border-[var(--parchment)]/30 bg-white/10 text-center font-display text-4xl tracking-[0.5em] text-[var(--parchment)] outline-none focus:border-[var(--brass)]" />
          {err && <p data-testid="pin-error" className="mt-2 text-sm text-[#f3b6a3]">{err}</p>}
          <button data-testid="pin-submit" disabled={busy || !pin} className="tap mt-4 h-14 w-full rounded-full bg-[var(--parchment)] text-base font-semibold text-[var(--jade)] disabled:opacity-50">{busy ? "Checking…" : "Open the trip"}</button>
          <p className="mt-6 text-[11px] text-[var(--parchment)]/50">One shared PIN for the family. This phone will stay unlocked.</p>
        </form>
      </Brand>
    );
  }
  if (trip && !profile) return <ProfilePicker />;
  return children;
}
